import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { issueNoticeService } from '@/services/issue-notice.service';
import type { IssueNoticeLive } from '@/types/issue-notice';
import { compareNotices } from '@/components/issue-notices/issue-notice-meta';

export const ISSUE_LIVE_KEY = ['issue-notices', 'live'] as const;
const POLL_MS = 30_000;
const CACHE_KEY = 'box-os:issue-notices:last-live';

function readCache(): IssueNoticeLive | undefined {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as IssueNoticeLive) : undefined;
  } catch {
    return undefined;
  }
}

function writeCache(data: IssueNoticeLive) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // storage unavailable — the banner still works, just without an offline copy
  }
}

export interface IssueNoticesLiveState {
  data: IssueNoticeLive | undefined;
  /** True when showing the last known notices because the API is unreachable. */
  stale: boolean;
  /** serverNow − clientNow in ms; add to Date.now() for server time (TB-2). */
  serverOffsetMs: number;
  refetch: () => void;
}

/**
 * The one poll behind the banner, bell and alert cards (TB-3). React Query
 * dedupes the key, so every caller shares a single request every 30 s.
 */
export function useIssueNoticesLive(enabled = true): IssueNoticesLiveState {
  const [cached] = useState(readCache);
  const query = useQuery({
    queryKey: ISSUE_LIVE_KEY,
    queryFn: () => issueNoticeService.live(),
    enabled,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 10_000,
    retry: 1,
  });

  useEffect(() => {
    if (query.data) writeCache(query.data);
  }, [query.data]);

  const serverOffsetMs = useMemo(() => {
    if (!query.data) return 0;
    return Date.parse(query.data.serverNow) - query.dataUpdatedAt;
  }, [query.data, query.dataUpdatedAt]);

  const stale = !!query.error && !query.isFetching;
  const data = useMemo(() => {
    const d = query.data ?? (stale ? cached : undefined);
    if (!d) return undefined;
    // A cached copy may hold notices that have since ended; drop those.
    const now = Date.now() + serverOffsetMs;
    return { ...d, active: d.active.filter((n) => !n.endsAt || Date.parse(n.endsAt) > now).sort(compareNotices) };
  }, [query.data, stale, cached, serverOffsetMs]);

  return { data, stale, serverOffsetMs, refetch: () => void query.refetch() };
}

/** Re-renders on an interval; returns server-corrected now (ms). */
export function useServerNow(serverOffsetMs: number, intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now() + serverOffsetMs);
  useEffect(() => {
    setNow(Date.now() + serverOffsetMs);
    const id = window.setInterval(() => setNow(Date.now() + serverOffsetMs), intervalMs);
    return () => window.clearInterval(id);
  }, [serverOffsetMs, intervalMs]);
  return now;
}

/** Call after any write so the banner reflects it immediately. */
export function useInvalidateIssueNotices() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['issue-notices'] });
}
